import React from "react";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  useTheme,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import SectionSubheading from "../SectionSubheading";
import UploadPictures from "../UploadPictures";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { parse, isValid, format } from "date-fns";

/**
 * CoatingIdentification - Component for displaying and editing coating identification data with Formik
 */
const CoatingIdentification = ({
  viewOnly = false,
  values,
  errors,
  touched,
  setFieldValue,
}) => {
  const coatingData = values?.coatingIdentification || {};

  const handleChange = (field, value) => {
    if (viewOnly) return;
    setFieldValue(`coatingIdentification.${field}`, value);
  };

  // Handle picture changes from UploadPictures component
  const handlePictureChange = (updatedPictures) => {
    setFieldValue("coatingIdentification.drawingPictures", updatedPictures);
  };

  // All fields in order of appearance
  const fields = [
    {
      id: "timeApplication",
      label: "Time Application",
      value: coatingData.timeApplication || "",
      type: "date",
      required: true,
    },
    {
      id: "painterId",
      label: "Painter ID",
      value: coatingData.painterId || "",
      type: "text",
      required: true,
    },
    {
      id: "methodApplication",
      label: "Method Application",
      value: coatingData.methodApplication || "",
      type: "text",
      required: true,
    },
    {
      id: "brandNameColour",
      label: "Brand Name/Colour/ RAL",
      value: coatingData.brandNameColour || "",
      type: "text",
      required: true,
    },
    {
      id: "mixingRatio",
      label: "Mixing Ratio",
      value: coatingData.mixingRatio || "",
      type: "text",
      required: true,
    },
    {
      id: "partABatchNo",
      label: "PART A (Batch No.)",
      value: coatingData.partABatchNo || "",
      type: "text",
      required: true,
    },
    {
      id: "partADOM",
      label: "PART A (D.O.M)",
      value: coatingData.partADOM || "",
      type: "text",
      required: true,
    },
    {
      id: "partBBatchNo",
      label: "PART B (Batch No.)",
      value: coatingData.partBBatchNo || "",
      type: "text",
      required: true,
    },
    {
      id: "partBDOM",
      label: "PART B (D.O.M)",
      value: coatingData.partBDOM || "",
      type: "text",
      required: true,
    },
    {
      id: "thinnerBatchNo",
      label: "Thinner (Batch No.)",
      value: coatingData.thinnerBatchNo || "",
      type: "text",
      required: true,
    },
    {
      id: "thinnerDOM",
      label: "Thinner (D.O.M)",
      value: coatingData.thinnerDOM || "",
      type: "text",
      required: true,
    },
    {
      id: "surfaceCondition",
      label: "Surface Condition (no sharp corner, slag, irregular,etc)",
      value: coatingData.surfaceCondition || "",
      type: "text",
      required: true,
    },
    {
      id: "stripeCoat",
      label: "Stripe Coat",
      value: coatingData.stripeCoat || "",
      type: "text",
      required: true,
    },
    {
      id: "wetFilmThickness",
      label: "Wet Film Thickness",
      value: coatingData.wetFilmThickness || "",
      type: "number",
      required: true,
      unit: "μm",
    },
    {
      id: "dryFilmThickness",
      label: "Dry Film Thickness (Total)",
      value: coatingData.dryFilmThickness || "",
      type: "number",
      required: true,
      unit: "μm",
    },
    {
      id: "adhesionTest",
      label: "Adhesion Test",
      value: coatingData.adhesionTest || "",
      type: "text",
      required: true,
    },
    {
      id: "fullCure",
      label: "Full Cure",
      checked: coatingData.fullCure || false,
      type: "checkbox",
      required: false,
    },
    {
      id: "dry",
      label: "Dry",
      checked: coatingData.dry || false,
      type: "checkbox",
      required: false,
    },
    {
      id: "drawingForSpecificRequirement",
      label: "Drawing For Specific Requirement",
      value: coatingData.drawingForSpecificRequirement || "",
      type: "text",
      required: true,
    },
    {
      id: "dftSpotTakenEvery",
      label: "DFT Spot Taken Every",
      value: coatingData.dftSpotTakenEvery || "",
      type: "text",
      required: true,
    },
    {
      id: "drawingPictures",
      label: "Upload Pictures",
      value: coatingData.drawingPictures || [],
      type: "upload",
      required: true,
    },
    {
      id: "remarks",
      label: "Remarks",
      value: coatingData.remarks || "",
      type: "textarea",
      required: true,
    },
  ];

  const theme = useTheme();

  // Styles following AmbientSurfaceConditionTable pattern
  const styles = {
    fieldRow: {
      display: "flex",
      mb: 2,
    },
    label: {
      width: { xs: 130, md: 160 },
      flexShrink: 0,
      color: theme.palette.text.primary,
      fontWeight: 500,
      fontSize: "0.875rem",
      mr: 2,
    },
    textField: {
      width: "100%",
      minWidth: 150,
      // maxWidth: 400,
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
      },
      "& .MuiInputBase-input": {
        fontSize: "0.875rem",
      },
      "& .MuiFormHelperText-root": {
        fontSize: "0.75rem",
        mt: 0.5,
      },
    },
    dateField: {
      width: "100%",
      minWidth: 150,
      maxWidth: 400,
      "& .MuiOutlinedInput-root": {
        backgroundColor: "transparent",
      },
      "& .MuiInputBase-input": {
        fontSize: "0.875rem",
      },
      "& .MuiFormHelperText-root": {
        fontSize: "0.75rem",
        mt: 0.5,
      },
      "& .MuiPickersSectionList-root": {
        width: "100%",
      },
    },
  };

  return (
    <Box sx={{ mt: 4 }}>
      <SectionSubheading title="Coating Identification" />
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Grid container spacing={2} sx={{ mt: 2, minWidth: 670 }}>
          {fields.map((field) => (
            <Grid
              key={field.id}
              size={{
                xs:
                  field.type === "textarea" || field.type === "upload" ? 12 : 6,
                sm:
                  field.type === "textarea" || field.type === "upload" ? 12 : 6,
                md:
                  field.type === "textarea" || field.type === "upload" ? 12 : 6,
              }}
            >
              <Box
                sx={{
                  ...styles.fieldRow,
                  alignItems:
                    field.type === "checkbox" ? "center" : "flex-start",
                }}
              >
                <Typography variant="body2" sx={styles.label}>
                  {field.label}
                  {field.required && (
                    <Box
                      component="span"
                      sx={{ color: theme.palette.error.main, ml: 0.5 }}
                    >
                      *
                    </Box>
                  )}
                </Typography>
                <Box sx={{ width: "100%" }}>
                  {(() => {
                    const fieldErrors = errors?.coatingIdentification || {};
                    const fieldTouched = touched?.coatingIdentification || {};
                    const hasError =
                      fieldErrors[field.id] && fieldTouched[field.id];

                    if (field.id === "drawingPictures") {
                      return (
                        <Box sx={{ mt: 1 }}>
                          <UploadPictures
                            viewOnly={viewOnly}
                            pictures={coatingData.drawingPictures || []}
                            onChange={handlePictureChange}
                            label=""
                            labelWidth={0}
                            maxImages={5}
                          />
                          {fieldErrors.drawingPictures &&
                            fieldTouched.drawingPictures && (
                              <FormHelperText error sx={{ mt: 1, ml: 0 }}>
                                {fieldErrors.drawingPictures}
                              </FormHelperText>
                            )}
                        </Box>
                      );
                    }

                    if (field.type === "textarea") {
                      return (
                        <TextField
                          size="small"
                          value={field.value}
                          onChange={(e) =>
                            handleChange(field.id, e.target.value)
                          }
                          disabled={viewOnly}
                          error={hasError}
                          helperText={hasError ? fieldErrors[field.id] : ""}
                          multiline
                          minRows={2}
                          sx={styles.textField}
                        />
                      );
                    }

                    if (field.id === "timeApplication") {
                      return (
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={enGB}
                        >
                          <DatePicker
                            disabled={viewOnly}
                            value={(() => {
                              const v = field.value;
                              if (!v) return null;
                              const parsed = parse(v, "dd/MM/yyyy", new Date());
                              return isValid(parsed) ? parsed : new Date(v);
                            })()}
                            onChange={(newVal) => {
                              if (viewOnly) return;
                              if (!newVal || !isValid(newVal)) {
                                handleChange(field.id, "");
                                return;
                              }
                              const formattedStr = format(newVal, "dd/MM/yyyy");
                              handleChange(field.id, formattedStr);
                            }}
                            format="dd/MM/yyyy"
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: styles.dateField,
                                error: hasError,
                                helperText: hasError
                                  ? fieldErrors[field.id]
                                  : "",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      );
                    }

                    if (
                      field.type === "text" ||
                      field.type === "number" ||
                      field.type === "date"
                    ) {
                      return (
                        <TextField
                          size="small"
                          type={field.type}
                          value={field.value}
                          onChange={(e) => {
                            const value =
                              field.type === "number"
                                ? parseFloat(e.target.value) || ""
                                : e.target.value;
                            handleChange(field.id, value);
                          }}
                          disabled={viewOnly}
                          error={hasError}
                          helperText={hasError ? fieldErrors[field.id] : ""}
                          InputProps={{
                            endAdornment: field.unit ? (
                              <InputAdornment position="end">
                                {field.unit}
                              </InputAdornment>
                            ) : null,
                          }}
                          sx={styles.textField}
                        />
                      );
                    }

                    return (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.checked}
                            onChange={(e) => {
                              if (viewOnly) return;
                              handleChange(field.id, e.target.checked);
                            }}
                            disabled={viewOnly}
                            size="small"
                          />
                        }
                        label=""
                        sx={{ width: 200 }}
                      />
                    );
                  })()}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default CoatingIdentification;
